import {
    Row,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import Download from '~/icons/iconography/download.svg';
import { ShopTypes } from '~/lib/data-contract';
import { useUserProfile } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Table } from '~/templates/blocks/shared/Table/Table';
import getAccountStatementUrl from '../helper/getAccountStatementUrl';
import { StyledMonthText, StyledText, TableCellLink } from '../styles';

type ExtendedIAccountStatement = ShopTypes.IAccountStatement & {
    monthDate?: string;
    fullDate?: string;
};

const columnHelper = createColumnHelper<ExtendedIAccountStatement>();
const select = [12, 24, 48];

export default function StatementTable({
    data,
}: {
    data: ExtendedIAccountStatement[] | undefined;
}) {
    const { translate } = useTranslations();

    const { data: profile } = useUserProfile();
    const customerId = profile?.user?.currentCustomerId;

    const columns = useMemo(
        () => [
            columnHelper.accessor('dateTimeOffset', {
                header: 'Dato',
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <StyledText>
                            {!value
                                ? ''
                                : new Date(value).toLocaleDateString('en-GB').replace(/\//g, '-')}
                        </StyledText>
                    );
                },
                size: 120,
                sortUndefined: -1,
            }),
            columnHelper.accessor('monthDate', {
                header: 'Måned',
                cell: (info) => {
                    const value = info.getValue();
                    return <StyledMonthText>{value}</StyledMonthText>;
                },
                size: 120,
                sortUndefined: -1,
                meta: {
                    isText: true,
                },
            }),
            {
                id: 'link',
                header: translate('mit-fog.download'),
                cell: ({ row }: { row: Row<ShopTypes.IAccountStatement> }) => {
                    const rowData = row.original;

                    const url = getAccountStatementUrl(customerId, rowData.year, rowData.month);
                    if (!url) return null;

                    const handleClick = (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (url) {
                            window.location.href = url;
                        } else {
                            console.error('Invalid customer ID or order number');
                        }
                    };

                    return (
                        <TableCellLink
                            customIcon={<Download />}
                            withIcon
                            href={url}
                            onClick={handleClick}
                        >
                            {translate('mit-fog.accountStatement.accountStatement')}
                        </TableCellLink>
                    );
                },
                size: 500,
                meta: {
                    isText: true,
                },
            },
        ],
        [customerId, translate],
    );

    const table = useReactTable({
        data: data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: select[0],
            },
            sorting: [
                {
                    id: 'dateTimeOffset',
                    desc: true,
                },
            ],
        },
    });
    /* @ts-ignore */
    return <Table table={table} data={data ?? []} select={select} />;
}
